import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitId } from '@/api/queries/besluit';
import { useBesluitType, useBesluitTypeId } from '@/api/queries/besluitType';
import { Button } from '@/components/Button/Button';
import { DocumentTitle } from '@/components/DocumentTitle';
import { ModalTrigger } from '@/components/Modal';
import { Nav } from '@/components/Nav/Nav';
import { Navbar } from '@/components/Navbar/Navbar';
import { TooltipWhenOverflowing } from '@/components/TooltipWhenOverflowing/TooltipWhenOverflowing';
import { eStrings } from '@/constants/strings';
import { RegularClockRotateLeft } from '@/icons/components';
import { SelectBesluit } from '@/modals/SelectBesluit';
import { twJoin } from '@/utils/tw-merge';

import { Body } from '../-components/Body';
import { Header } from '../-components/Header';
import useViewerStore from '../-utils/useViewerStore';
import {
  ensureQueryDataViewerBesluitHoofdstukken,
  useViewerBesluitHoofdstukken,
} from './-utils/useViewerBesluitHoofdstukken';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout'
)({
  loader: async ({ params, context: { queryClient } }) => {
    const [latestBedrijf, besluit] = await Promise.all([
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(params.bedrijfLineageId)
      ),
      queryClient.ensureQueryData(useBesluitId.getOptions(params.besluitId)),
      queryClient.ensureQueryData(useBesluitType.getOptions({})),
      ensureQueryDataViewerBesluitHoofdstukken({
        besluitId: params.besluitId,
        queryClient,
      }),
    ]);

    if (!latestBedrijf || !besluit) throw notFound();
  },
  component: BesluitIdComponent,
  onLeave: () => {
    useViewerStore.getState().setShowAllMeta(false);
  },
});

/**
 * Route component
 */
function BesluitIdComponent() {
  const { bedrijfLineageId, besluitId } = Route.useParams();
  const { hoofdstukId } = useParams({ strict: false });

  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const besluit = useBesluitId(besluitId, {
    // After a besluit is published, it will never change again.
    staleTime: Infinity,
  });
  const besluitType = useBesluitTypeId(besluit.data?.Type ?? '', {
    enabled: !!besluit.data?.Type,
  });

  const { grondslagen, isLoading: isLoadingHoofdstukken } =
    useViewerBesluitHoofdstukken({ besluitId });

  /**
   * Make sure the grondslag of the current hoofdstuk page is open when a user lands on the page
   */
  const [openGrondslagen, setOpenGrondslagen] = useState<string[]>([]);

  useEffect(() => {
    const currentHoofdstukGrondslag = hoofdstukId
      ? grondslagen
          .flatMap(({ hoofdstukken }) => hoofdstukken)
          .find((hoofdstuk) => hoofdstuk.data.ID === hoofdstukId)?.grondslag
      : undefined;

    if (currentHoofdstukGrondslag) {
      setOpenGrondslagen((prev) =>
        prev.includes(currentHoofdstukGrondslag.data.id)
          ? prev
          : [...prev, currentHoofdstukGrondslag.data.id]
      );
    }
  }, [grondslagen, hoofdstukId]);

  if (!bedrijf.data || !besluit.data || isLoadingHoofdstukken) return null;

  const currentGrondslag = grondslagen.find(({ hoofdstukken }) =>
    hoofdstukken.find(({ data: { ID } }) => ID === hoofdstukId)
  );

  const defaultOpen = [];

  if (currentGrondslag) defaultOpen.push(currentGrondslag.data.id);

  return (
    <>
      <DocumentTitle
        sections={[
          eStrings.LBL_DIGI_V,
          bedrijf.data.Naam ?? '',
          `${besluit.data.Zaaknummer} (${besluitType.data?.Naam})`,
        ]}
      />

      <Navbar />

      <Header bedrijf={bedrijf.data} mode="besluit">
        <ModalTrigger
          modalComponent={SelectBesluit}
          onOpen={() => ({
            bedrijf: bedrijf.data!,
            activeBesluitId: besluitId,
          })}
          asChild
        >
          <Button className="w-[180px] justify-center">Kies een besluit</Button>
        </ModalTrigger>
      </Header>

      <Body>
        <Body.Sidebar ariaLabel="Besluit navigatie">
          <Nav
            aria-labelledby="inhoudsopgave-id"
            value={openGrondslagen}
            onValueChange={setOpenGrondslagen}
            withAccordion
          >
            <Nav.Header as="h2" id="inhoudsopgave-id">
              Inhoudsopgave
            </Nav.Header>

            <Nav.List>
              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/besluittekst"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Besluittekst
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/rechtsmiddelen"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Rechtsmiddelen
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              {grondslagen.map((grondslag) => (
                <Nav.ListItem key={`viewer-layout-${grondslag.data.id}`}>
                  <Nav.Tree
                    value={grondslag.data.id}
                    label={grondslag.data.label}
                    triggerButtonProps={{
                      icon: grondslag.data.icon,
                      className: '!font-bold',
                    }}
                  >
                    <Nav.List>
                      {grondslag.hoofdstukken.map((hoofdstuk) => (
                        <Nav.ListItem
                          key={`viewer-layout-hoofdstuk-${hoofdstuk.data.ID}`}
                        >
                          <Nav.Button
                            className={twJoin(
                              hoofdstuk.data.Stop_Lineage && 'line-through'
                            )}
                            asChild
                          >
                            {hoofdstuk.hasVoorschriften ? (
                              <Link
                                to="/$bedrijfLineageId/viewer/besluit/$besluitId/voorschriften/$hoofdstukId"
                                params={{
                                  besluitId,
                                  bedrijfLineageId,
                                  hoofdstukId: hoofdstuk.data.ID,
                                }}
                                activeProps={{
                                  // Prevent this link from showing an "active" state when its the current page.
                                  className: undefined,
                                }}
                              >
                                <span className="flex size-full items-center gap-1">
                                  <span>{hoofdstuk.data.Nummer}</span>
                                  <TooltipWhenOverflowing
                                    description={hoofdstuk.data.Titel}
                                    asHTML
                                    side="top"
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: hoofdstuk.data.Titel,
                                      }}
                                    />
                                  </TooltipWhenOverflowing>
                                  <span>
                                    {hoofdstuk.data.ObjectStatus ===
                                      'Hersteld' && <RegularClockRotateLeft />}
                                  </span>
                                </span>
                              </Link>
                            ) : (
                              <Link
                                to="/$bedrijfLineageId/viewer/besluit/$besluitId/inhoudelijke-overwegingen/$hoofdstukId"
                                params={{
                                  besluitId,
                                  bedrijfLineageId,
                                  hoofdstukId: hoofdstuk.data.ID,
                                }}
                                activeProps={{
                                  // Prevent this link from showing an "active" state when its the current page.
                                  className: undefined,
                                }}
                              >
                                <span className="flex size-full items-center gap-1">
                                  <span>{hoofdstuk.data.Nummer}</span>
                                  <TooltipWhenOverflowing
                                    description={hoofdstuk.data.Titel}
                                    asHTML
                                    side="top"
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: hoofdstuk.data.Titel,
                                      }}
                                    />
                                  </TooltipWhenOverflowing>
                                  <span>
                                    {hoofdstuk.data.ObjectStatus ===
                                      'Hersteld' && <RegularClockRotateLeft />}
                                  </span>
                                </span>
                              </Link>
                            )}
                          </Nav.Button>

                          {hoofdstukId === hoofdstuk.data.ID && (
                            <Nav.List className="pl-2 pt-1">
                              {hoofdstuk.hasVoorschriften && (
                                <Nav.ListItem>
                                  <Nav.Button asChild>
                                    <Link
                                      to="/$bedrijfLineageId/viewer/besluit/$besluitId/voorschriften/$hoofdstukId"
                                      params={{
                                        besluitId,
                                        bedrijfLineageId,
                                        hoofdstukId: hoofdstuk.data.ID,
                                      }}
                                    >
                                      Voorschriften
                                    </Link>
                                  </Nav.Button>
                                </Nav.ListItem>
                              )}

                              <Nav.ListItem>
                                <Nav.Button asChild>
                                  <Link
                                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/inhoudelijke-overwegingen/$hoofdstukId"
                                    params={{
                                      besluitId,
                                      bedrijfLineageId,
                                      hoofdstukId: hoofdstuk.data.ID,
                                    }}
                                  >
                                    Inhoudelijke overwegingen
                                  </Link>
                                </Nav.Button>
                              </Nav.ListItem>
                            </Nav.List>
                          )}
                        </Nav.ListItem>
                      ))}
                    </Nav.List>
                  </Nav.Tree>
                </Nav.ListItem>
              ))}

              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/procedurele-overwegingen"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Procedurele overwegingen
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/algemeen-toetsingskader"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Algemeen toetsingskader
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/overige-overwegingen"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Overige overwegingen
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/begrippen"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Begrippen
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button asChild>
                  <Link
                    to="/$bedrijfLineageId/viewer/besluit/$besluitId/bijlage"
                    params={{ besluitId, bedrijfLineageId }}
                  >
                    Bijlage
                  </Link>
                </Nav.Button>
              </Nav.ListItem>
            </Nav.List>
          </Nav>
        </Body.Sidebar>

        <Body.Content className="pb-10">
          <Outlet />
        </Body.Content>
      </Body>
    </>
  );
}
